import {
  FilterLiveSearch,
  SavedQueriesList,
  FilterLiveForm,
  useGetList,
  FilterList,
  FilterListItem,
} from "react-admin";
import { Autocomplete, TextField, Stack } from "@mui/material";
import { useFormContext, Controller } from "react-hook-form";
import { Redeem, Category, CloudDone } from "@mui/icons-material";
import { StyledListAside } from "@/components/ui/react-admin/StyledListAside";
import { ResourceName } from "@/types";

const ClientFilterInput = () => {
  const { control, setValue } = useFormContext();

  const { data: clients, isLoading } = useGetList(ResourceName.CLIENTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "client_legal_name", order: "ASC" },
  });

  return (
    <Controller
      name="client_id_ref"
      control={control}
      render={({ field }) => (
        <Autocomplete
          options={clients || []}
          getOptionLabel={(option) => option.client_legal_name || ""}
          value={clients?.find((c) => c.id === field.value) || null}
          onChange={(_, newValue) => {
            field.onChange(newValue?.id || null);
            // Clear dependent filters when client changes
            setValue("claimant_id_ref", null);
            setValue("policy_id_ref", null);
          }}
          loading={isLoading}
          size="small"
          renderInput={(params) => <TextField {...params} label="Client" variant="outlined" />}
          sx={{ width: "100%" }}
        />
      )}
    />
  );
};

const ClaimantFilterInput = () => {
  const { control, watch } = useFormContext();
  const selectedClientId = watch("client_id_ref");

  const { data: claimants, isLoading } = useGetList(ResourceName.CLAIMANTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "claimant_name", order: "ASC" },
    filter: selectedClientId ? { client_id_ref: selectedClientId } : {},
  });

  return (
    <Controller
      name="claimant_id_ref"
      control={control}
      render={({ field }) => (
        <Autocomplete
          options={claimants || []}
          getOptionLabel={(option) => option.claimant_name || ""}
          value={claimants?.find((c) => c.id === field.value) || null}
          onChange={(_, newValue) => {
            field.onChange(newValue?.id || null);
          }}
          loading={isLoading}
          size="small"
          renderInput={(params) => <TextField {...params} label="Claimant" variant="outlined" />}
          sx={{ width: "100%" }}
        />
      )}
    />
  );
};

const CascadingFilters = () => {
  const { watch } = useFormContext();
  const selectedClientId = watch("client_id_ref");

  return (
    <Stack spacing={2}>
      <ClientFilterInput />
      {selectedClientId && <ClaimantFilterInput />}
    </Stack>
  );
};

export const StoplossClaimListAside = () => {
  return (
    <StyledListAside>
      <FilterLiveSearch />
      <SavedQueriesList />

      <FilterLiveForm>
        <CascadingFilters />
      </FilterLiveForm>

      <FilterList label="Claim Status" icon={<Redeem />}>
        <FilterListItem label="New" value={{ claim_status: "new" }} />
        <FilterListItem label="In Process" value={{ claim_status: "in_process" }} />
        <FilterListItem label="Submitted" value={{ claim_status: "submitted" }} />
        <FilterListItem label="Partially Reimbursed" value={{ claim_status: "partially_reimbursed" }} />
        <FilterListItem label="Reimbursed" value={{ claim_status: "reimbursed" }} />
        <FilterListItem label="Denied" value={{ claim_status: "denied" }} />
        <FilterListItem label="Pended" value={{ claim_status: "pended" }} />
      </FilterList>

      <FilterList label="Claim Type" icon={<Category />}>
        <FilterListItem label="Medical" value={{ stoploss_claim_type: "medical" }} />
        <FilterListItem label="Rx" value={{ stoploss_claim_type: "rx" }} />
      </FilterList>

      <FilterList label="Aura Submission" icon={<CloudDone />}>
        <FilterListItem label="Submitted to Aura" value={{ submitted_to_aura: true }} />
        <FilterListItem label="Not Submitted" value={{ submitted_to_aura: false }} />
      </FilterList>
    </StyledListAside>
  );
};
